import React, { useState, useEffect } from "react";
import { API, getToken, can } from "../api";

// ───────────────────────────────────────────────────────────────────────────
// Claim-level BDX readiness chip — SCOPED.  Renders ONLY when the claim is in
// the bordereau profile's scope (backend returns {in_scope:false} otherwise).
// No global noise: ordinary adjusters lack view_bordereau_reports and never
// even fire the request.  Shows a compact Ready/Warning/Error chip; hover/click
// reveals the issue list with next-action hints.
// ───────────────────────────────────────────────────────────────────────────

const SEM = {
  ready:   { bg: "#E6F4EA", fg: "#1E7E34", dot: "#1E7E34", label: "BDX Ready" },
  warning: { bg: "#FFF4CE", fg: "#8A6D00", dot: "#B7902A", label: "BDX Warning" },
  error:   { bg: "#FBE4E4", fg: "#A12121", dot: "#A12121", label: "BDX Error" },
};

export default function BordereauReadinessChip({ claimId }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!claimId || !can("view_bordereau_reports")) return;
    let alive = true;
    fetch(`${API}/claims/${claimId}/bordereau-readiness?template_id=1`,
      { headers: { Authorization: `Bearer ${getToken()}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (alive) setData(d); })
      .catch(() => {});
    return () => { alive = false; };
  }, [claimId]);

  // Not in BDX scope (or no permission / not loaded) → render nothing.
  if (!data || !data.in_scope) return null;
  const s = SEM[data.status] || SEM.warning;

  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      <button onClick={() => setOpen((o) => !o)}
        title={data.issue_count ? `${data.issue_count} item(s) — click for detail` : "All bordereau data present"}
        style={{ display: "inline-flex", alignItems: "center", gap: 6, background: s.bg, color: s.fg,
          border: "none", borderRadius: 14, padding: "3px 11px", fontWeight: 700, fontSize: 12, cursor: "pointer" }}>
        <span style={{ width: 7, height: 7, borderRadius: "50%", background: s.dot, display: "inline-block" }} />
        {s.label}{data.issue_count ? ` · ${data.issue_count}` : ""}
      </button>
      {open && (
        <div style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, zIndex: 50, width: 320,
          background: "white", border: "1px solid #e2e8f0", borderRadius: 8, boxShadow: "0 6px 24px rgba(0,0,0,0.12)", padding: 14 }}>
          <div style={{ fontSize: 12, color: "#64748b", marginBottom: 8 }}>
            Bordereau readiness · <b>{data.profile_name}</b>
          </div>
          {data.issue_count === 0 ? (
            <div style={{ color: SEM.ready.fg, fontWeight: 600, fontSize: 13 }}>All required bordereau data is present.</div>
          ) : (
            <ul style={{ margin: 0, paddingLeft: 16, fontSize: 13, lineHeight: 1.5 }}>
              {data.issues.map((i, n) => (
                <li key={n} style={{ marginBottom: 6 }}>
                  <span style={{ color: SEM[i.severity]?.fg || "#8A6D00", fontWeight: 700 }}>
                    {(i.severity || "warning").toUpperCase()}
                  </span>{" — "}{i.field}
                  {i.action && <div style={{ color: "#64748b", fontSize: 12 }}>→ {i.action}</div>}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
